// ConsumeQuickPickModal.js
import React, { useState, useEffect } from 'react';
import { Modal } from '../../shared';
import { EatItemModal } from './eatItemModal.js';
import { EatRecipeModal } from './eatRecipeModal.js'; // NEW

const ConsumeQuickPickModal = ({ title, fetcher, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [buttons, setButtons] = useState([]); // {type: 'item'|'recipe', id, label}

  // view state
  const [screen, setScreen] = useState('quickpick'); // 'quickpick' | 'eatItem' | 'eatRecipe'
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedRecipeName, setSelectedRecipeName] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetcher();

        // Normalize common shapes:
        // { msg, data: { rows: { items:[], recipes:[] } } }
        // { data: { items:[], recipes:[] } }
        // { items:[], recipes:[] }
        const payload =
          resp?.data?.rows ??
          resp?.data ??
          resp ??
          {};

        const items   = Array.isArray(payload.items)   ? payload.items   : [];
        const recipes = Array.isArray(payload.recipes) ? payload.recipes : [];

        const normalizedItems = items.map(i => ({
          type: 'item',
          id: i?.itemID ?? i?.id ?? String(i?.itemName ?? i?.name ?? 'item'),
          label: i?.itemName ?? i?.name ?? 'Item',
        }));

        // recipes may be strings OR objects—support both
        const normalizedRecipes = recipes.map(r => {
          if (typeof r === 'string') {
            return {
              type: 'recipe',
              id: r,           // use the name itself as the id
              label: r,
            };
          }
          return {
            type: 'recipe',
            id: r?.recipeID ?? r?.id ?? String(r?.recipeName ?? r?.name ?? 'recipe'),
            label: r?.recipeName ?? r?.name ?? 'Recipe',
          };
        });

        const normalized = [...normalizedItems, ...normalizedRecipes].slice(0, 8);

        if (!cancelled) setButtons(normalized);
      } catch (e) {
        console.error('quick pick fetch error', e);
        if (!cancelled) setError('Failed to load recommendations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fetcher]);

  const COLORS = {
    item:   { bg: '#7E57C2', text: '#FFFFFF' }, // purple
    recipe: { bg: '#26A69A', text: '#FFFFFF' }, // teal
  };

  const headerTitle =
    screen === 'eatItem' ? 'Eat Item'
    : screen === 'eatRecipe' ? 'Eat Recipe'
    : title;

  return (
    <Modal title={headerTitle} onClose={onClose}>
      {screen === 'eatItem' ? (
        <div style={{ maxWidth: 580 }}>
          <EatItemModal
            initialItemId={selectedItemId}
            useInternalModal={false}   // render only the form content
            onClose={() => {
              // Go back to quick picks without closing the entire modal
              setScreen('quickpick');
            }}
            onSubmit={(payload) => {
              console.log('EatItem submitted from quick pick:', payload);
              onClose?.(); // close the overall modal after success; tweak if you want to stay
            }}
          />
        </div>
      ) : screen === 'eatRecipe' ? (
        <div style={{ maxWidth: 580 }}>
          <EatRecipeModal
            initialRecipeName={selectedRecipeName}
            useInternalModal={false}   // render only the form content
            onClose={() => {
              setScreen('quickpick');
            }}
            onSubmit={(payload) => {
              console.log('EatRecipe submitted from quick pick:', payload);
              onClose?.();
            }}
          />
        </div>
      ) : (
        <>
          {loading && <div style={{ opacity: 0.7, fontStyle: 'italic' }}>Loading…</div>}
          {error && <div style={{ color: '#b00020' }}>{error}</div>}
          {!loading && !error && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 10,
                maxWidth: 580, // ~ 4 columns of ~140 + gaps fits nicely, 8 total looks good
              }}
            >
              {buttons.map(btn => {
                const c = COLORS[btn.type] || COLORS.item;
                return (
                  <button
                    key={`${btn.type}-${btn.id}`}
                    onClick={() => {
                      if (btn.type === 'item') {
                        setSelectedItemId(String(btn.id));
                        setScreen('eatItem');
                      } else if (btn.type === 'recipe') {
                        // NEW: open EatRecipe flow
                        setSelectedRecipeName(String(btn.label)); // label is the recipe name
                        setScreen('eatRecipe');
                      }
                    }}
                    style={{
                      background: c.bg,
                      color: c.text,
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      minHeight: 44,
                    }}
                  >
                    {btn.label}
                  </button>
                );
              })}
              {buttons.length === 0 && (
                <div style={{ opacity: 0.7, fontStyle: 'italic' }}>No suggestions.</div>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export { ConsumeQuickPickModal };
